/**
 * Warm Transfer Selector
 *
 * Derives the consultation state a warm transfer is in, so the on-screen row,
 * the call-actions grid and the keyboard shortcuts all read the same rules
 * instead of each re-deriving them from `warmTransferContext`.
 */
import type { SoftphoneStore } from '..';

export interface WarmTransferConsultation {
  /**
   * The consultation is the call on screen, so it owns the call actions:
   * Transfer completes the handover and Merge is off-limits.
   */
  isActive: boolean;
  /**
   * A person picked up the consultation (voicemail doesn't count — there's
   * nobody to consult with). This is the screen with the green Transfer button,
   * so it's also when the blocked actions start explaining themselves.
   */
  isAnswered: boolean;
  /**
   * The transfer can be handed over right now. Mirrors exactly when the
   * consultation's Transfer button is visible and enabled, so the shortcut
   * can't fire while the button says otherwise.
   */
  canComplete: boolean;
}

export const selectWarmTransferConsultation = (
  state: SoftphoneStore
): WarmTransferConsultation => {
  const { warmTransferContext, activeCalls, currentCallId } = state;
  const consultationCallId = warmTransferContext?.consultationCallId;

  // Only the consultation's own state matters here; the call object itself is
  // never handed out, so nothing downstream can drift from these rules.
  const call = consultationCallId ? activeCalls.get(consultationCallId) : null;

  const isActive = !!consultationCallId && consultationCallId === currentCallId;
  const isAnswered =
    call?.state === 'active' && call?.answeredByVoicemail !== true;

  return {
    isActive,
    isAnswered,
    canComplete: isAnswered && !state.isCompletingTransfer,
  };
};
